import React from "react";
import styled, { css } from "styled-components";

import { CleaningProduct as CleaningProductInterface } from "@typings/booking";

import { rem } from "@ui/helpers";
import { TestWrapper, Checkbox } from "@ui/Atoms";

interface CleaningProductProps extends CleaningProductInterface {
  containerTestId?: string;
  specialPriceApplied?: boolean;
  checked: boolean;
  freeInfoLabelText: string;
  onChange: (
    event: React.SyntheticEvent<HTMLInputElement>,
    label: string,
  ) => void;
}

export const CleaningProduct = (
  props: CleaningProductProps,
): React.ReactElement => {
  return (
    <TestWrapper testId={props.containerTestId}>
      <Product key={`cleaning-product-${props.productId}`}>
        <Checkbox
          name={props.productId}
          onChange={e => {
            props.onChange(e, props.name);
          }}
          checked={props.checked}
          label={
            <ProductWrapper>
              <ProductImage>
                <ProductImageWrapper>
                  <img src={props.imageUrl} alt={props.name} />
                </ProductImageWrapper>
              </ProductImage>

              <ProductContent>
                <div>
                  <ProductHeading>
                    <strong>{props.name}</strong>
                    <ProductHeadingPrice>
                      {props.currency}{" "}
                      <ProductPriceAmount
                        specialPriceApplied={props.specialPriceApplied}
                        specialPrice={props.specialPrice}
                      >
                        {props.regularPrice}
                      </ProductPriceAmount>
                    </ProductHeadingPrice>
                  </ProductHeading>
                  {props.specialPrice !== null && (
                    <ProductSpecialPriceInfo>
                      {props.freeInfoLabelText}
                    </ProductSpecialPriceInfo>
                  )}
                </div>

                <ProductDescription
                  specialPriceApplied={props.specialPriceApplied}
                >
                  {props.description}
                </ProductDescription>
              </ProductContent>

              <ProductPrice>
                <strong>
                  {props.currency}{" "}
                  <ProductPriceAmount
                    specialPriceApplied={props.specialPriceApplied}
                    specialPrice={props.specialPrice}
                  >
                    {props.regularPrice}
                  </ProductPriceAmount>
                </strong>
              </ProductPrice>
            </ProductWrapper>
          }
        />
      </Product>
    </TestWrapper>
  );
};

const Product = styled.li`
  list-style: none;
`;

const ProductWrapper = styled.div`
  display: flex;

  font-size: ${props => props.theme.fonts.sizes.caption};
  font-weight: ${props => props.theme.fonts.weights.normal};
  line-height: ${props => props.theme.fonts.lineHeights.caption};
`;

const ProductImage = styled.div`
  padding: 0 ${props => props.theme.margins.base_x2} 0
    ${props => props.theme.margins.base};

  ${props => props.theme.breakpoints.tablet} {
    padding: 0 ${props => props.theme.margins.base_x2};
  }
`;

const ProductImageWrapper = styled.div`
  position: relative;
  width: ${rem(56)};
  border-radius: ${props => props.theme.border.radius.half};
  overflow: hidden;

  ${props => props.theme.breakpoints.tablet} {
    width: ${rem(64)};
  }
`;

const ProductContent = styled.div`
  flex: 1 1 100%;
`;

const ProductHeading = styled.div`
  font-size: ${props => props.theme.fonts.sizes.subtitle2};
  line-height: ${rem(20)};
  display: flex;
  justify-content: space-between;

  ${props => props.theme.breakpoints.tablet} {
    display: inline;
  }
`;

const ProductHeadingPrice = styled.strong`
  padding-left: ${props => props.theme.margins.base_x2};
  flex: 0 0 auto;
  display: inline;

  ${props => props.theme.breakpoints.tablet} {
    display: none;
  }
`;

const ProductSpecialPriceInfo = styled.span`
  margin-left: 0;
  color: ${props => props.theme.palette.blue500};

  ${props => props.theme.breakpoints.tablet} {
    margin-left: ${props => props.theme.margins.base};
  }
`;

const ProductPrice = styled.div`
  padding-left: ${props => props.theme.margins.base_x5};
  font-size: ${props => props.theme.fonts.sizes.subtitle2};
  line-height: ${rem(20)};
  flex: 0 0 auto;
  display: none;

  ${props => props.theme.breakpoints.tablet} {
    display: block;
  }
`;

const ProductPriceAmount = styled.span<{
  specialPriceApplied?: boolean;
  specialPrice: number | null;
}>`
  ${props =>
    props.specialPriceApplied &&
    css`
      position: relative;
      text-decoration: line-through;

      &::after {
        content: "${props.specialPrice
          ?.toFixed(2)
          .toString()
          .replace(".00", ".-")}";
        position: absolute;
        top: 100%;
        right: 0;
        color: ${props.theme.palette.blue500};
      }
    `}
`;

const ProductDescription = styled.div<{ specialPriceApplied?: boolean }>`
  margin: ${props => props.theme.margins.base} 0 0;

  ${props =>
    props.specialPriceApplied &&
    css`
      padding-right: ${props.theme.margins.base_x3};

      ${props => props.theme.breakpoints.tablet} {
        padding-right: 0;
      }
    `}
`;
